import { useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  MdOutlineIntegrationInstructions,
  MdNorth,
  MdSouth,
} from "react-icons/md";
import { EmblaCarouselType } from "embla-carousel";
import { ScratchInstructions } from "utils/interfaces";
import DotNavigationMap from "components/utils/DotNavigationMap";

interface PagesControllerProps {
  emblaApi?: EmblaCarouselType;
  instructions?: ScratchInstructions[];
  activeFullCodeImage: string;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  setShowCodeFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
  setInitialCodeTab: React.Dispatch<React.SetStateAction<number>>;
}

// Parameters
const DURATION = 0.3;

const PagesController: React.FC<PagesControllerProps> = ({
  emblaApi,
  instructions,
  activeFullCodeImage,
  selectedIndex,
  setSelectedIndex,
  setShowCodeFullscreen,
  setInitialCodeTab,
}) => {
  /**
   * Function used when clicking over the "View full code so far" button
   */
  const handleViewFullCode = () => {
    // Open the fullscreen mode
    setShowCodeFullscreen(true);

    // And specify the first tab (current step)
    setInitialCodeTab(1);
  };
  /**
   * Function to apply when the down button is clicked
   */
  const handleDownArrowClick = useCallback(() => {
    // Check of the emblaApi is defined
    if (!emblaApi) return;

    // Define the new state
    const newIndex = Math.min(
      instructions ? instructions.length : 0,
      selectedIndex + 1
    );

    // Move to the index of interest
    emblaApi.scrollTo(newIndex);
    // And update the state
    setSelectedIndex(newIndex);
  }, [emblaApi, selectedIndex]);

  /**
   * Function to apply when the up button is clicked
   */
  const handleUpArrowClick = useCallback(() => {
    // Check of the emblaApi is defined
    if (!emblaApi) return;

    // Define the new state
    const newIndex = Math.max(0, selectedIndex - 1);

    // Move to the index of interest
    emblaApi.scrollTo(newIndex);
    // And update the state
    setSelectedIndex(newIndex);
  }, [emblaApi, selectedIndex]);

  return (
    <div className="h-12 justify-between gap-4 items-center inline-flex">
      <DotNavigationMap
        numItems={instructions ? instructions.length : 0}
        currentItem={selectedIndex}
        maxNumDotsToShow={7}
      />
      <div className="self-stretch h-full justify-end items-center gap-4 inline-flex">
        <AnimatePresence>
          {activeFullCodeImage && (
            <motion.div
              initial={{ opacity: 0, width: "0px" }}
              animate={{ opacity: 1, width: "100%" }}
              exit={{ opacity: 0, width: "0px" }}
              className="overflow-hidden"
            >
              <div
                className="h-full p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-start items-center gap-2 inline-flex cursor-pointer"
                onClick={handleViewFullCode}
              >
                <div className="text-secondary-600 text-sm font-semibold font-poppins leading-[17px] select-none whitespace-nowrap">
                  View Full Code so Far
                </div>
                <MdOutlineIntegrationInstructions className="w-6 h-6 fill-secondary-600" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="inline-flex">
          <AnimatePresence>
            {selectedIndex > 0 && (
              <motion.div
                initial={{ opacity: 0, width: "0px" }}
                animate={{ opacity: 1, width: "48px" }}
                exit={{
                  opacity: 0,
                  width: "0px",
                  transition: { duration: DURATION, ease: "easeOut" },
                }}
                transition={{ duration: DURATION, ease: "easeOut" }}
                className="z-0"
                onClick={handleUpArrowClick}
              >
                <div className="p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-center items-center inline-flex cursor-pointer">
                  <MdNorth className="w-6 h-6 fill-secondary-600" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {/* Add this "gap" to make the movement smooth */}
          <AnimatePresence>
            {selectedIndex > 0 &&
              selectedIndex < (instructions ? instructions.length - 1 : 0) && (
                <motion.div
                  initial={{ opacity: 0, width: "0px" }}
                  animate={{ opacity: 1, width: "16px" }}
                  exit={{
                    opacity: 0,
                    width: "0px",
                    transition: { duration: DURATION, ease: "easeOut" },
                  }}
                  transition={{ duration: DURATION, ease: "easeOut" }}
                />
              )}
          </AnimatePresence>
          <AnimatePresence>
            {selectedIndex < (instructions ? instructions.length - 1 : 0) && (
              <motion.div
                initial={{ opacity: 0, width: "0px" }}
                animate={{ opacity: 1, width: "48px" }}
                exit={{
                  opacity: 0,
                  width: "0px",
                  transition: { duration: DURATION, ease: "easeOut" },
                }}
                transition={{ duration: DURATION, ease: "easeOut" }}
                onClick={handleDownArrowClick}
                className="z-10"
              >
                <div className="p-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded-full justify-center items-center inline-flex cursor-pointer">
                  <MdSouth className="w-6 h-6 fill-white" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default PagesController;
